import * as Yup from "yup";
import { isValid, parse, addDays, parseISO, format } from "date-fns";
import localize from "./localize";

export const cyrillicString = /^(|^[а-яёА-ЯЁ'\-\–\s]+)$/;
export const maskedPhoneNumber = /^[+7]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;
export const maskedPhoneNumberFormatted = /(^8|7|\+7)((\d{10})|(\s\(\d{3}\)\s\d{3}\s\d{2}\s\d{2}))/;
export const maskedDate = /^[0-9\.]+$/;
export const maskedSeria = /\d{4}/;
export const maskedPostalCode = /\d{6}/;
export const maskedRegionCode = /\d{2}/;
export const maskedPass = /\d{6}/;
const maskedInn = /\d{12}/;
const maskSnils = /^[0-9\s\-]+$/;

const checkData = (value: string | null | undefined): boolean => {
    if (!value) return true;
    const arrDate = value.split(".");
    const isntFull = arrDate.some((date) => Number.isNaN(Number.parseInt(date, 10)));
    if (isntFull && value === "__.__.____") return true;
    const date = parse(value, "dd.MM.yyyy", new Date());
    return isValid(date);
};

const checkBirthDay = (value: string): boolean => {
    if (!value) return true;
    const arrDate = value.split(".");
    const isntFull = arrDate.some((date) => Number.isNaN(Number.parseInt(date, 10)));
    if (isntFull && value === "__.__.____") return true;
    const minDate = parse("01.01.1900", "dd.MM.yyyy", new Date());
    const maxDate = addDays(new Date(), -1);
    const date = parse(value, "dd.MM.yyyy", new Date());
    return date > minDate && date < maxDate;
};

const toDate = (value: string): Date | null => {
    if (!value) return null;

    const arrDate = value.split(".");
    const isntFull = arrDate.some((date) => Number.isNaN(Number.parseInt(date, 10)));
    if (isntFull && value === "__.__.____") return null;
    const date = parse(value, "dd.MM.yyyy", new Date());
    return isValid(date) ? date : null;
};

export const toDateFormat = (dateString?: string | null): string => {
    try {
        if (!dateString) return "";

        const date = parseISO(dateString);
        return format(date, "dd.MM.yyyy");
    } catch (e) {
        return "";
    }
};

export const birthDayValid = Yup.string()
    .required(localize.RequiredField)
    .test("checkData", "Неверный формат даты", checkData)
    .test("checkBirthDay", "Недопустимое значение", checkBirthDay);

export const tillDateValid = Yup.string()
    .required(localize.RequiredField)
    .test("tillDate", "Дата окончания не может быть меньше даты начала", function (value, context) {
        // @ts-ignore
        return toDate(value) >= toDate(context.parent.sinceDate);
    });

export const numberValuePositive = Yup.number().min(1, "Значение должно быть больше нуля");

export const cyrillicYupRequired = Yup.string()
    .max(100, `${localize.TooLongText} 100`)
    .required(localize.RequiredField);

export const cyrillicCityRequired = Yup.string()
    .matches(cyrillicString, localize.InvalidSymbols)
    .max(100, `${localize.TooLongText} 100`)
    .min(2)
    .required(localize.RequiredField);

export const cyrillicNameRequired = Yup.string()
    .matches(cyrillicString, localize.InvalidSymbols)
    .test("minTwoWords", "Поле должно содержать имя и фамилию", (value) => {
        if (!value) return false;
        const words = value.trim().split(/\s+/);
        return words.length >= 2;
    })
    .max(100, `${localize.TooLongText} 100`)
    .required(localize.RequiredField);

export const cyrillicYupNonRequired = Yup.string()
    .matches(cyrillicString, localize.InvalidSymbols)
    .max(100, `${localize.TooLongText} 100`)
    .optional();

export const phoneRequired = Yup.string()
    .matches(maskedPhoneNumber, localize.InvalidMaskSymbols)
    .required(localize.RequiredField);

export const phoneFormattedRequired = Yup.string()
    .matches(maskedPhoneNumberFormatted, localize.InvalidMaskSymbols)
    .required(localize.RequiredField);

export const phoneNotRequired = Yup.string().nullable().matches(maskedPhoneNumber, localize.InvalidMaskSymbols);

export const postalCodeRequired = Yup.string()
    .matches(maskedPostalCode, localize.InvalidMaskSymbols)
    .required(localize.RequiredField);

export const regionCodeRequired = Yup.string()
    .matches(maskedRegionCode, localize.InvalidMaskSymbols)
    .required(localize.RequiredField);

export const emailRequired = Yup.string()
    .email(localize.InvalidEmail)
    .matches(/\..+$/, localize.InvalidEmail)
    .required(localize.RequiredField);

export const fieldRequired = Yup.string().required(localize.RequiredField);

export const radioRequired = Yup.string().required(localize.RequiredAny);

export const maskedDateYupRequired = Yup.string()
    .matches(maskedDate, localize.InvalidMaskSymbols)
    .required(localize.RequiredField)
    .test("checkData", "Неверный формат даты", checkData);

export const maskedDateYupNotRequired = Yup.string()
    .matches(maskedDate, { message: localize.InvalidMaskSymbols, excludeEmptyString: true })
    .test("checkData", "Неверный формат даты", checkData)
    .optional();

export const seriaRequired = Yup.string()
    .matches(maskedSeria, localize.InvalidMaskSymbols)
    .required(localize.RequiredField)
    .test("checkSeria", "Недопустимое значение", (value) => value > "0000");

export const numPassRequired = Yup.string()
    .matches(maskedPass, localize.InvalidMaskSymbols)
    .required(localize.RequiredField)
    .test("checkNumPass", "Недопустимое значение", (value) => value > "000000");

export const innReq = Yup.string().matches(maskedInn, localize.InvalidMaskSymbols).required(localize.RequiredField);

export const snilsReq = Yup.string().matches(maskSnils, localize.InvalidMaskSymbols).required(localize.RequiredField);
